import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Col, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import jwtDecode from 'jwt-decode';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAAssinatura, CadernoLPMAPlantioSoja } from '../../models/modelsCadernoLPMA';
import { DecodedToken } from '../../models/user';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import { Context } from '../../context/AuthContext';
import { isAdmin } from '../../users/CadernoLPMA/isAdmin';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';
import CoordenadaDmsField from './components/CoordenadaDmsField';
import { formatCoordenadaDmsMask } from '../../util/cadernoLPMA/coordenadaDmsMask';
import ComentarioExpandivel from './components/ComentarioExpandivel';
import MetodoLimpezaSelect from './components/MetodoLimpezaSelect';
import {
	formatarMetodoLimpezaParaJson,
	normalizarMetodoLimpezaParaFormulario,
} from '../../util/cadernoLPMA/metodoLimpeza';

interface PlantioSojaModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAPlantioSoja) => void;
	initialData?: CadernoLPMAPlantioSoja;
	assinaturasDisponiveis?: CadernoLPMAAssinatura[];
}

type FormValues = CadernoLPMAPlantioSoja & Record<string, any>;

const defaultValues: Partial<FormValues> = {
	id: 0,
	cadernoLPMAId: 0,
	identificacaoPlantio: '',
	dataInicioPlantio: null,
	dataTerminoPlantio: null,
	assinaturaEnvolvidosBase64: null,
	tipoPlantioAprovadoMecanico: false,
	tipoPlantioAprovadoManual: false,
	tipoPlantioRealizadoMecanico: false,
	tipoPlantioRealizadoManual: false,
	talhao: '',
	detalhamentoPlantioJson: null,
	resumoOgmAprovadoM2: null,
	resumoComercialAprovadoM2: null,
	resumoTotalAprovadoM2: null,
	resumoOgmPlantadoM2: null,
	resumoComercialPlantadoM2: null,
	resumoTotalPlantadoM2: null,
	distanciaIsolamentoExigida: null,
	distanciaIsolamentoMantida: null,
	larguraBordaduraExigida: null,
	larguraBordaduraPlantada: null,
	materialPlantadoBordadura: '',
	coordenadaP01: '',
	coordenadaP02: '',
	coordenadaP03: '',
	coordenadaP04: '',
	coordenadaP05: '',
	coordenadaP06: '',
	coordenadaP07: '',
	coordenadaP08: '',
	coordenadaP09: '',
	coordenadaP10: '',
	coordenadaP11: '',
	coordenadaP12: '',
	limpezaMaquinariosJson: null,
	dataComunicacaoInicioPlantio: null,
	dataComunicacaoTerminoPlantio: null,
	observacoesInformacoesAdicionais: '',
	checklistPlacaBiosseguranca: false,
	checklistPlacaBiossegurancaObservacao: '',
	checklistEstacasDelimitacao: false,
	checklistEstacasDelimitacaoObservacao: '',
	checklistComunicadoCroqui: false,
	checklistComunicadoCroquiObservacao: '',
	checklistEnvelopesDestruidos: false,
	checklistEnvelopesDestruidosObservacao: '',
	checklistRoupasEquipamentosLimpos: false,
	checklistRoupasEquipamentosLimposObservacao: '',
	checklistPlantio2PlacaBiosseguranca: false,
	checklistPlantio2PlacaBiossegurancaObservacao: '',
	checklistPlantio2EstacasDelimitacao: false,
	checklistPlantio2EstacasDelimitacaoObservacao: '',
	checklistPlantio2ComunicadoCroqui: false,
	checklistPlantio2ComunicadoCroquiObservacao: '',
	checklistPlantio2EnvelopesDestruidos: false,
	checklistPlantio2EnvelopesDestruidosObservacao: '',
	checklistPlantio2RoupasEquipamentosLimpos: false,
	checklistPlantio2RoupasEquipamentosLimposObservacao: '',
	rubricaEquipeTecnica: '',
	dataRubricaEquipeTecnica: null,
	formulario2DataInicioPlantio: null,
	formulario2DataTerminoPlantio: null,
	formulario2AssinaturaEnvolvidosBase64: null,
	formulario2TipoPlantioAprovadoMecanico: false,
	formulario2TipoPlantioAprovadoManual: false,
	formulario2TipoPlantioRealizadoMecanico: false,
	formulario2TipoPlantioRealizadoManual: false,
	formulario2Talhao: '',
	formulario2DetalhamentoPlantioJson: null,
	formulario2ResumoOgmAprovadoM2: null,
	formulario2ResumoComercialAprovadoM2: null,
	formulario2ResumoTotalAprovadoM2: null,
	formulario2ResumoOgmPlantadoM2: null,
	formulario2ResumoComercialPlantadoM2: null,
	formulario2ResumoTotalPlantadoM2: null,
	formulario2DistanciaIsolamentoExigida: null,
	formulario2DistanciaIsolamentoMantida: null,
	formulario2LarguraBordaduraExigida: null,
	formulario2LarguraBordaduraPlantada: null,
	formulario2MaterialPlantadoBordadura: '',
	formulario2CoordenadaP01: '',
	formulario2CoordenadaP02: '',
	formulario2CoordenadaP03: '',
	formulario2CoordenadaP04: '',
	formulario2CoordenadaP05: '',
	formulario2CoordenadaP06: '',
	formulario2CoordenadaP07: '',
	formulario2CoordenadaP08: '',
	formulario2CoordenadaP09: '',
	formulario2CoordenadaP10: '',
	formulario2CoordenadaP11: '',
	formulario2CoordenadaP12: '',
	formulario2LimpezaMaquinariosJson: null,
	formulario2DataComunicacaoInicioPlantio: null,
	formulario2DataComunicacaoTerminoPlantio: null,
	formulario2ObservacoesInformacoesAdicionais: '',
};

const coordinateFields: Array<{ name: keyof CadernoLPMAPlantioSoja; label: string }> = [
	{ name: 'coordenadaP01', label: 'P01 (S / W)' },
	{ name: 'coordenadaP02', label: 'P02 (S / W)' },
	{ name: 'coordenadaP03', label: 'P03 (S / W)' },
	{ name: 'coordenadaP04', label: 'P04 (S / W)' },
	{ name: 'coordenadaP05', label: 'P05 (S / W)' },
	{ name: 'coordenadaP06', label: 'P06 (S / W)' },
	{ name: 'coordenadaP07', label: 'P07 (S / W)' },
	{ name: 'coordenadaP08', label: 'P08 (S / W)' },
	{ name: 'coordenadaP09', label: 'P09 (S / W)' },
	{ name: 'coordenadaP10', label: 'P10 (S / W)' },
	{ name: 'coordenadaP11', label: 'P11 (S / W)' },
	{ name: 'coordenadaP12', label: 'P12 (S / W)' },
];

const coordinateFieldsFormulario2: Array<{ name: keyof CadernoLPMAPlantioSoja; label: string }> = [
	{ name: 'formulario2CoordenadaP01', label: 'P01 (S / W)' },
	{ name: 'formulario2CoordenadaP02', label: 'P02 (S / W)' },
	{ name: 'formulario2CoordenadaP03', label: 'P03 (S / W)' },
	{ name: 'formulario2CoordenadaP04', label: 'P04 (S / W)' },
	{ name: 'formulario2CoordenadaP05', label: 'P05 (S / W)' },
	{ name: 'formulario2CoordenadaP06', label: 'P06 (S / W)' },
	{ name: 'formulario2CoordenadaP07', label: 'P07 (S / W)' },
	{ name: 'formulario2CoordenadaP08', label: 'P08 (S / W)' },
	{ name: 'formulario2CoordenadaP09', label: 'P09 (S / W)' },
	{ name: 'formulario2CoordenadaP10', label: 'P10 (S / W)' },
	{ name: 'formulario2CoordenadaP11', label: 'P11 (S / W)' },
	{ name: 'formulario2CoordenadaP12', label: 'P12 (S / W)' },
];

const COORDENADAS_MIN = 4;
const COORDENADAS_MAX = coordinateFields.length;

const calcularQtdeCoordenadasVisiveis = (
	dados: Record<string, any> | null | undefined,
	fields: Array<{ name: keyof CadernoLPMAPlantioSoja; label: string }>,
): number => {
	if (!dados) {
		return COORDENADAS_MIN;
	}

	let maiorComValor = COORDENADAS_MIN;
	fields.forEach((field, index) => {
		const valor = dados[field.name as string];
		if (valor !== undefined && valor !== null && String(valor).trim() !== '') {
			maiorComValor = Math.max(maiorComValor, index + 1);
		}
	});

	return Math.min(Math.max(maiorComValor, COORDENADAS_MIN), fields.length);
};

const parseDateValue = (valor: any): Date | null => {
	if (!valor) {
		return null;
	}

	const data = valor instanceof Date ? valor : new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data;
};

const parseJsonArray = (valor: any): Array<Record<string, any>> => {
	if (!valor) {
		return [];
	}

	if (Array.isArray(valor)) {
		return valor as Array<Record<string, any>>;
	}

	if (typeof valor === 'string') {
		try {
			const parsed = JSON.parse(valor);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}

	if (typeof valor === 'object') {
		return [valor as Record<string, any>];
	}

	return [];
};

const camposDetalhamento = [
	{ chave: 'talhao', campo: 'detalhamentoTalhao' },
	{ chave: 'experimento', campo: 'detalhamentoExperimento' },
	{ chave: 'tipoMaterialGmNumeroEventosAprovado', campo: 'detalhamentoTipoMaterialGmNumeroEventosAprovado' },
	{ chave: 'tipoMaterialGmNumeroEventosPlantado', campo: 'detalhamentoTipoMaterialGmNumeroEventosPlantado' },
	{ chave: 'tipoMaterialGmNumeroRepeticoesAprovado', campo: 'detalhamentoTipoMaterialGmNumeroRepeticoesAprovado' },
	{ chave: 'tipoMaterialGmNumeroRepeticoesPlantado', campo: 'detalhamentoTipoMaterialGmNumeroRepeticoesPlantado' },
	{ chave: 'tipoMaterialGmNumeroParcelasAprovado', campo: 'detalhamentoTipoMaterialGmNumeroParcelasAprovado' },
	{ chave: 'tipoMaterialGmNumeroParcelasPlantado', campo: 'detalhamentoTipoMaterialGmNumeroParcelasPlantado' },
	{ chave: 'tipoMaterialGmTamanhoParcelasAprovado', campo: 'detalhamentoTipoMaterialGmTamanhoParcelasAprovado' },
	{ chave: 'tipoMaterialGmTamanhoParcelasPlantado', campo: 'detalhamentoTipoMaterialGmTamanhoParcelasPlantado' },
	{ chave: 'tipoMaterialGmAreaTotalAprovado', campo: 'detalhamentoTipoMaterialGmAreaTotalAprovado' },
	{ chave: 'tipoMaterialGmAreaTotalPlantado', campo: 'detalhamentoTipoMaterialGmAreaTotalPlantado' },
	{ chave: 'tipoMaterialWtOutrasNumeroEventosAprovado', campo: 'detalhamentoTipoMaterialWtOutrasNumeroEventosAprovado' },
	{ chave: 'tipoMaterialWtOutrasNumeroEventosPlantado', campo: 'detalhamentoTipoMaterialWtOutrasNumeroEventosPlantado' },
	{ chave: 'tipoMaterialWtOutrasNumeroRepeticoesAprovado', campo: 'detalhamentoTipoMaterialWtOutrasNumeroRepeticoesAprovado' },
	{ chave: 'tipoMaterialWtOutrasNumeroRepeticoesPlantado', campo: 'detalhamentoTipoMaterialWtOutrasNumeroRepeticoesPlantado' },
	{ chave: 'tipoMaterialWtOutrasNumeroParcelasAprovado', campo: 'detalhamentoTipoMaterialWtOutrasNumeroParcelasAprovado' },
	{ chave: 'tipoMaterialWtOutrasNumeroParcelasPlantado', campo: 'detalhamentoTipoMaterialWtOutrasNumeroParcelasPlantado' },
	{ chave: 'tipoMaterialWtOutrasTamanhoParcelasAprovado', campo: 'detalhamentoTipoMaterialWtOutrasTamanhoParcelasAprovado' },
	{ chave: 'tipoMaterialWtOutrasTamanhoParcelasPlantado', campo: 'detalhamentoTipoMaterialWtOutrasTamanhoParcelasPlantado' },
	{ chave: 'tipoMaterialWtOutrasAreaTotalAprovado', campo: 'detalhamentoTipoMaterialWtOutrasAreaTotalAprovado' },
	{ chave: 'tipoMaterialWtOutrasAreaTotalPlantado', campo: 'detalhamentoTipoMaterialWtOutrasAreaTotalPlantado' },
	{ chave: 'numeroEventosAprovado', campo: 'detalhamentoNumeroEventosAprovado' },
	{ chave: 'numeroEventosPlantado', campo: 'detalhamentoNumeroEventosPlantado' },
	{ chave: 'numeroTratamentosAprovado', campo: 'detalhamentoNumeroTratamentosAprovado' },
	{ chave: 'numeroTratamentosPlantado', campo: 'detalhamentoNumeroTratamentosPlantado' },
	{ chave: 'numeroRepeticoesAprovado', campo: 'detalhamentoNumeroRepeticoesAprovado' },
	{ chave: 'numeroRepeticoesPlantado', campo: 'detalhamentoNumeroRepeticoesPlantado' },
	{ chave: 'numeroParcelasAprovado', campo: 'detalhamentoNumeroParcelasAprovado' },
	{ chave: 'numeroParcelasPlantado', campo: 'detalhamentoNumeroParcelasPlantado' },
	{ chave: 'tamanhoParcelasAprovado', campo: 'detalhamentoTamanhoParcelasAprovado' },
	{ chave: 'tamanhoParcelasPlantado', campo: 'detalhamentoTamanhoParcelasPlantado' },
	{ chave: 'areaTotalAprovado', campo: 'detalhamentoAreaTotalAprovado' },
	{ chave: 'areaTotalPlantado', campo: 'detalhamentoAreaTotalPlantado' },
] as const;

const camposLimpeza: Array<{ chave: string; campo: string; tipo?: 'data' }> = [
	{ chave: 'nome', campo: 'limpezaNome' },
	{ chave: 'dataEntrada', campo: 'limpezaDataEntrada', tipo: 'data' },
	{ chave: 'dataSaida', campo: 'limpezaDataSaida', tipo: 'data' },
	{ chave: 'metodo', campo: 'limpezaMetodo' },
	{ chave: 'responsavel', campo: 'limpezaResponsavel' },
];

const camposDetalhamentoFormulario2 = [
	{ chave: 'talhao', campo: 'formulario2DetalhamentoTalhao' },
	{ chave: 'experimento', campo: 'formulario2DetalhamentoExperimento' },
	...camposDetalhamento
		.filter(({ chave }) => chave.startsWith('tipoMaterial'))
		.map(({ chave, campo }) => ({ chave, campo: `formulario2${campo.charAt(0).toUpperCase()}${campo.slice(1)}` })),
	{ chave: 'numeroEventosAprovado', campo: 'formulario2DetalhamentoNumeroEventosAprovado' },
	{ chave: 'numeroEventosPlantado', campo: 'formulario2DetalhamentoNumeroEventosPlantado' },
	{ chave: 'numeroTratamentosAprovado', campo: 'formulario2DetalhamentoNumeroTratamentosAprovado' },
	{ chave: 'numeroTratamentosPlantado', campo: 'formulario2DetalhamentoNumeroTratamentosPlantado' },
	{ chave: 'numeroRepeticoesAprovado', campo: 'formulario2DetalhamentoNumeroRepeticoesAprovado' },
	{ chave: 'numeroRepeticoesPlantado', campo: 'formulario2DetalhamentoNumeroRepeticoesPlantado' },
	{ chave: 'numeroParcelasAprovado', campo: 'formulario2DetalhamentoNumeroParcelasAprovado' },
	{ chave: 'numeroParcelasPlantado', campo: 'formulario2DetalhamentoNumeroParcelasPlantado' },
	{ chave: 'tamanhoParcelasAprovado', campo: 'formulario2DetalhamentoTamanhoParcelasAprovado' },
	{ chave: 'tamanhoParcelasPlantado', campo: 'formulario2DetalhamentoTamanhoParcelasPlantado' },
	{ chave: 'areaTotalAprovado', campo: 'formulario2DetalhamentoAreaTotalAprovado' },
	{ chave: 'areaTotalPlantado', campo: 'formulario2DetalhamentoAreaTotalPlantado' },
] as const;

const camposLimpezaFormulario2: Array<{ chave: string; campo: string; tipo?: 'data' }> = [
	{ chave: 'nome', campo: 'formulario2LimpezaNome' },
	{ chave: 'dataEntrada', campo: 'formulario2LimpezaDataEntrada', tipo: 'data' },
	{ chave: 'dataSaida', campo: 'formulario2LimpezaDataSaida', tipo: 'data' },
	{ chave: 'metodo', campo: 'formulario2LimpezaMetodo' },
	{ chave: 'responsavel', campo: 'formulario2LimpezaResponsavel' },
];

const camposDataPrincipais: Array<keyof FormValues> = [
	'dataInicioPlantio',
	'dataTerminoPlantio',
	'dataComunicacaoInicioPlantio',
	'dataComunicacaoTerminoPlantio',
	'dataRubricaEquipeTecnica',
	'formulario2DataInicioPlantio',
	'formulario2DataTerminoPlantio',
	'formulario2DataComunicacaoInicioPlantio',
	'formulario2DataComunicacaoTerminoPlantio',
];

const camposBooleanosPrincipais: Array<keyof FormValues> = [
	'tipoPlantioAprovadoMecanico',
	'tipoPlantioAprovadoManual',
	'tipoPlantioRealizadoMecanico',
	'tipoPlantioRealizadoManual',
	'formulario2TipoPlantioAprovadoMecanico',
	'formulario2TipoPlantioAprovadoManual',
	'formulario2TipoPlantioRealizadoMecanico',
	'formulario2TipoPlantioRealizadoManual',
	'checklistPlacaBiosseguranca',
	'checklistEstacasDelimitacao',
	'checklistComunicadoCroqui',
	'checklistEnvelopesDestruidos',
	'checklistRoupasEquipamentosLimpos',
	'checklistPlantio2PlacaBiosseguranca',
	'checklistPlantio2EstacasDelimitacao',
	'checklistPlantio2ComunicadoCroqui',
	'checklistPlantio2EnvelopesDestruidos',
	'checklistPlantio2RoupasEquipamentosLimpos',
];

const hasAnyValue = (entry: Record<string, any>): boolean =>
	Object.values(entry).some(value => value !== undefined && value !== null && value !== '');

export default function PlantioSojaModal({
	show,
	onHide,
	onSubmit,
	initialData,
	assinaturasDisponiveis = [],
}: PlantioSojaModalProps) {
	const { token } = useContext(Context);
	const usuarioAdmin = useMemo(() => {
		if (!token?.access_token) {
			return false;
		}
		try {
			const decoded = jwtDecode<DecodedToken>(token.access_token);
			return isAdmin(decoded?.roles ?? []);
		} catch {
			return false;
		}
	}, [token?.access_token]);

	const {
		register,
		handleSubmit,
		control,
		reset,
		getValues,
		setValue,
		watch,
	} = useForm<FormValues>({
		mode: 'all',
		defaultValues,
		shouldUnregister: false,
	});

	const [qtdeDetalhamento, setQtdeDetalhamento] = useState<number>(1);
	const [qtdeLimpeza, setQtdeLimpeza] = useState<number>(1);
	const [qtdeCoordenadas, setQtdeCoordenadas] = useState<number>(COORDENADAS_MIN);
	const [qtdeDetalhamentoFormulario2, setQtdeDetalhamentoFormulario2] = useState<number>(1);
	const [qtdeLimpezaFormulario2, setQtdeLimpezaFormulario2] = useState<number>(1);
	const [qtdeCoordenadasFormulario2, setQtdeCoordenadasFormulario2] = useState<number>(COORDENADAS_MIN);

	const tipoPlantioAprovadoMecanico = watch('tipoPlantioAprovadoMecanico');
	const tipoPlantioAprovadoManual = watch('tipoPlantioAprovadoManual');
	const tipoPlantioRealizadoMecanico = watch('tipoPlantioRealizadoMecanico');
	const tipoPlantioRealizadoManual = watch('tipoPlantioRealizadoManual');
	const formulario2TipoPlantioAprovadoMecanico = watch('formulario2TipoPlantioAprovadoMecanico');
	const formulario2TipoPlantioAprovadoManual = watch('formulario2TipoPlantioAprovadoManual');
	const formulario2TipoPlantioRealizadoMecanico = watch('formulario2TipoPlantioRealizadoMecanico');
	const formulario2TipoPlantioRealizadoManual = watch('formulario2TipoPlantioRealizadoManual');

	const abasPlantioSoja = [
		{ valor: 2 as 1 | 2 | 3 | 4 | 5, label: 'Instalação do ensaio e localização' },
		{ valor: 4 as 1 | 2 | 3 | 4 | 5, label: 'Limpeza' },
		{ valor: 5 as 1 | 2 | 3 | 4 | 5, label: 'Comunicação' },
		{ valor: 3 as 1 | 2 | 3 | 4 | 5, label: 'Checklist' },
	];

	const [abaPlantioSoja, setAbaPlantioSoja] = useState<1 | 2 | 3 | 4 | 5>(2);
	const abasPlantioSojaFormulario2 = [
		{ valor: 2 as 1 | 2 | 3 | 4 | 5, label: 'Instalação do ensaio e localização' },
		{ valor: 4 as 1 | 2 | 3 | 4 | 5, label: 'Limpeza' },
		{ valor: 5 as 1 | 2 | 3 | 4 | 5, label: 'Comunicação' },
	];
	const [abaPlantioSojaFormulario2, setAbaPlantioSojaFormulario2] = useState<1 | 2 | 3 | 4 | 5>(2);
	const exibirFormulario2Plantio = false;

	// Estados para assinatura
	const [assinaturaEnvolvidos, setAssinaturaEnvolvidos] = useState<string | null>(null);
	const [assinaturaFormulario2Envolvidos, setAssinaturaFormulario2Envolvidos] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [mostrarAssinaturaFormulario2, setMostrarAssinaturaFormulario2] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);
	const [nomesAdicionaisForm2, setNomesAdicionaisForm2] = useState<string[]>([]);

	// Carregar assinatura do usuário
	useEffect(() => {
		const carregarAssinaturaUsuario = async () => {
			try {
				const token = localStorage.getItem('token');
				if (token) {
					const decoded: any = JSON.parse(atob(token.split('.')[1]));
					const username = decoded.unique_name || '';

					const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
					if (data && data.assinaturaBase64) {
						setAssinaturaUsuario(data.assinaturaBase64);
					}
				}
			} catch (error) {
				console.error('Erro ao carregar assinatura:', error);
			}
		};

		if (show) {
			carregarAssinaturaUsuario();

			// Carregar assinaturas salvas se existirem
			if (initialData?.assinaturaEnvolvidosBase64) {
				setAssinaturaEnvolvidos(initialData.assinaturaEnvolvidosBase64);
			} else {
				setAssinaturaEnvolvidos(null);
			}
			if (initialData?.formulario2AssinaturaEnvolvidosBase64) {
				setAssinaturaFormulario2Envolvidos(initialData.formulario2AssinaturaEnvolvidosBase64);
			} else {
				setAssinaturaFormulario2Envolvidos(null);
			}
			setNomesAdicionais(parseNomesEnvolvidosJson(initialData?.nomesEnvolvidosAdicionaisJson));
			setNomesAdicionaisForm2(parseNomesEnvolvidosJson(initialData?.formulario2NomesEnvolvidosAdicionaisJson));
		} else {
			// Limpar estados ao fechar
			setAssinaturaEnvolvidos(null);
			setAssinaturaFormulario2Envolvidos(null);
			setMostrarAssinatura(false);
			setMostrarAssinaturaFormulario2(false);
			setNomesAdicionais([]);
			setNomesAdicionaisForm2([]);
		}
	}, [show, initialData]);

	const handleMudarAbaPlantioSoja = (aba: 1 | 2 | 3 | 4 | 5) => {
		console.log('Mudando aba, valores atuais:', getValues());
		setAbaPlantioSoja(aba);
	};
	const handleMudarAbaPlantioSojaFormulario2 = (aba: 1 | 2 | 3 | 4 | 5) => setAbaPlantioSojaFormulario2(aba);

	useEffect(() => {
		register('tipoPlantioAprovadoMecanico');
		register('tipoPlantioAprovadoManual');
		register('tipoPlantioRealizadoMecanico');
		register('tipoPlantioRealizadoManual');
		register('formulario2TipoPlantioAprovadoMecanico');
		register('formulario2TipoPlantioAprovadoManual');
		register('formulario2TipoPlantioRealizadoMecanico');
		register('formulario2TipoPlantioRealizadoManual');
	}, [register]);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setQtdeDetalhamento(1);
			setQtdeLimpeza(1);
			setQtdeCoordenadas(COORDENADAS_MIN);
			setQtdeDetalhamentoFormulario2(1);
			setQtdeLimpezaFormulario2(1);
			setQtdeCoordenadasFormulario2(COORDENADAS_MIN);
			setAbaPlantioSoja(2);
			setAbaPlantioSojaFormulario2(2);
			return;
		}

		setAbaPlantioSoja(2);
		setAbaPlantioSojaFormulario2(2);

		const dadosIniciais = initialData ?? null;
		console.log('@@ PlantioSojaModal useEffect -> show:', show, 'initialData:', dadosIniciais);
		const dadosIniciaisRegistro = dadosIniciais
			? (dadosIniciais as unknown as Record<string, any>)
			: undefined;

		const valoresConvertidos: Partial<FormValues> = {
			...defaultValues,
			...(dadosIniciais ?? {}),
		};

		camposDataPrincipais.forEach((campo) => {
			const valor = dadosIniciaisRegistro ? dadosIniciaisRegistro[campo as string] : undefined;
			valoresConvertidos[campo] = parseDateValue(valor);
		});

		camposBooleanosPrincipais.forEach((campo) => {
			const valor = dadosIniciaisRegistro ? dadosIniciaisRegistro[campo as string] : undefined;
			valoresConvertidos[campo] =
				valor !== undefined && valor !== null ? (!!valor as any) : valoresConvertidos[campo];
		});

		const coordenadasCamposDms: Array<keyof CadernoLPMAPlantioSoja> = [
			...coordinateFields.map((f) => f.name),
			...coordinateFieldsFormulario2.map((f) => f.name),
		];
		coordenadasCamposDms.forEach((campo) => {
			const v = valoresConvertidos[campo];
			if (v === undefined || v === null || v === '') {
				return;
			}
			(valoresConvertidos as any)[campo] = formatCoordenadaDmsMask(String(v));
		});

		reset(valoresConvertidos as FormValues);

		const linhasDetalhamento = parseJsonArray(dadosIniciais?.detalhamentoPlantioJson);
		setQtdeDetalhamento(Math.max(linhasDetalhamento.length, 1));
		linhasDetalhamento.forEach((linha, indice) => {
			camposDetalhamento.forEach(({ chave, campo }) => {
				let valor = linha?.[chave];
				if (chave === 'tipoMaterialGmNumeroEventosAprovado') {
					valor = valor ?? linha?.tipoMaterialGmAprovado1 ?? linha?.tipoMaterialGmAprovado ?? linha?.tipoMaterialGm;
				}
				if (chave === 'tipoMaterialGmNumeroEventosPlantado') {
					valor = valor ?? linha?.tipoMaterialGmPlantado1 ?? linha?.tipoMaterialGmPlantado;
				}
				if (chave === 'tipoMaterialWtOutrasNumeroEventosAprovado') {
					valor = valor ?? linha?.tipoMaterialWtOutrasAprovado1 ?? linha?.tipoMaterialWtOutrasAprovado ?? linha?.tipoMaterialWtOutras ?? linha?.tipoMaterial;
				}
				if (chave === 'tipoMaterialWtOutrasNumeroEventosPlantado') {
					valor = valor ?? linha?.tipoMaterialWtOutrasPlantado1 ?? linha?.tipoMaterialWtOutrasPlantado;
				}
				setValue(`${campo}${indice}` as any, valor ?? '', { shouldDirty: false });
			});
		});

		const linhasLimpeza = parseJsonArray(dadosIniciais?.limpezaMaquinariosJson);
		setQtdeLimpeza(Math.max(linhasLimpeza.length, 1));
		linhasLimpeza.forEach((linha, indice) => {
			camposLimpeza.forEach(({ chave, campo, tipo }) => {
				if (chave === 'metodo') {
					const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(linha?.metodo);
					setValue(`${campo}${indice}` as any, metodoLimpeza.metodo, { shouldDirty: false });
					setValue(`limpezaMetodoOutros${indice}` as any, metodoLimpeza.outros, { shouldDirty: false });
					return;
				}
				const valorOriginal = linha?.[chave];
				const valorTratado = tipo === 'data' ? parseDateValue(valorOriginal) : valorOriginal ?? '';
				setValue(`${campo}${indice}` as any, valorTratado ?? (tipo === 'data' ? null : ''), {
					shouldDirty: false,
				});
			});
		});

		const linhasDetalhamentoForm2 = parseJsonArray(dadosIniciais?.formulario2DetalhamentoPlantioJson);
		setQtdeDetalhamentoFormulario2(Math.max(linhasDetalhamentoForm2.length, 1));
		linhasDetalhamentoForm2.forEach((linha, indice) => {
			camposDetalhamentoFormulario2.forEach(({ chave, campo }) => {
				let valor = linha?.[chave];
				if (chave === 'tipoMaterialGmNumeroEventosAprovado') {
					valor = valor ?? linha?.tipoMaterialGmAprovado1 ?? linha?.tipoMaterialGmAprovado ?? linha?.tipoMaterialGm;
				}
				if (chave === 'tipoMaterialGmNumeroEventosPlantado') {
					valor = valor ?? linha?.tipoMaterialGmPlantado1 ?? linha?.tipoMaterialGmPlantado;
				}
				if (chave === 'tipoMaterialWtOutrasNumeroEventosAprovado') {
					valor = valor ?? linha?.tipoMaterialWtOutrasAprovado1 ?? linha?.tipoMaterialWtOutrasAprovado ?? linha?.tipoMaterialWtOutras ?? linha?.tipoMaterial;
				}
				if (chave === 'tipoMaterialWtOutrasNumeroEventosPlantado') {
					valor = valor ?? linha?.tipoMaterialWtOutrasPlantado1 ?? linha?.tipoMaterialWtOutrasPlantado;
				}
				setValue(`${campo}${indice}` as any, valor ?? '', { shouldDirty: false });
			});
		});

		const linhasLimpezaForm2 = parseJsonArray(dadosIniciais?.formulario2LimpezaMaquinariosJson);
		setQtdeLimpezaFormulario2(Math.max(linhasLimpezaForm2.length, 1));
		linhasLimpezaForm2.forEach((linha, indice) => {
			camposLimpezaFormulario2.forEach(({ chave, campo, tipo }) => {
				if (chave === 'metodo') {
					const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(linha?.metodo);
					setValue(`${campo}${indice}` as any, metodoLimpeza.metodo, { shouldDirty: false });
					setValue(`formulario2LimpezaMetodoOutros${indice}` as any, metodoLimpeza.outros, {
						shouldDirty: false,
					});
					return;
				}
				const valorOriginal = linha?.[chave];
				const valorTratado = tipo === 'data' ? parseDateValue(valorOriginal) : valorOriginal ?? '';
				setValue(`${campo}${indice}` as any, valorTratado ?? (tipo === 'data' ? null : ''), {
					shouldDirty: false,
				});
			});
		});

		setQtdeCoordenadas(calcularQtdeCoordenadasVisiveis(dadosIniciaisRegistro, coordinateFields));
		setQtdeCoordenadasFormulario2(
			calcularQtdeCoordenadasVisiveis(dadosIniciaisRegistro, coordinateFieldsFormulario2),
		);
	}, [show, initialData, reset, setValue]);

	const adicionarLinhaLimpeza = () => setQtdeLimpeza(prev => prev + 1);
	const removerLinhaLimpeza = () => setQtdeLimpeza(prev => (prev > 1 ? prev - 1 : prev));

	const adicionarCoordenada = () =>
		setQtdeCoordenadas(prev => (prev < COORDENADAS_MAX ? prev + 1 : prev));
	const removerCoordenada = () => {
		setQtdeCoordenadas(prev => {
			if (prev <= COORDENADAS_MIN) {
				return prev;
			}
			const campo = coordinateFields[prev - 1];
			setValue(campo.name as any, '', { shouldDirty: true });
			return prev - 1;
		});
	};

	const adicionarLinhaLimpezaFormulario2 = () => setQtdeLimpezaFormulario2(prev => prev + 1);
	const removerLinhaLimpezaFormulario2 = () => setQtdeLimpezaFormulario2(prev => (prev > 1 ? prev - 1 : prev));

	const adicionarCoordenadaFormulario2 = () =>
		setQtdeCoordenadasFormulario2(prev => (prev < COORDENADAS_MAX ? prev + 1 : prev));
	const removerCoordenadaFormulario2 = () => {
		setQtdeCoordenadasFormulario2(prev => {
			if (prev <= COORDENADAS_MIN) {
				return prev;
			}
			const campo = coordinateFieldsFormulario2[prev - 1];
			setValue(campo.name as any, '', { shouldDirty: true });
			return prev - 1;
		});
	};

	const getDisplayStyleAbaPlantio = (abaAtiva: 1 | 2 | 3 | 4 | 5, abaAtual: 1 | 2 | 3 | 4 | 5) => ({
		display: abaAtiva === abaAtual ? 'block' : 'none',
	});

	const getDisplayStyleAbaFormulario2 = (abaAtiva: 1 | 2 | 3 | 4 | 5, abaAtual: 1 | 2 | 3 | 4 | 5) => ({
		display: abaAtiva === abaAtual ? 'block' : 'none',
	});

	const renderCampoComparativo = (
		label: string,
		nomeAprovado: string,
		nomePlantado: string,
		options?: { step?: number },
	) => (
		<Form.Row className="row-custom">
			<Form.Group as={Col} md={12} className="mb-3">
				<Form.Label>{label}</Form.Label>
				<div className="d-flex flex-column flex-md-row" style={{ gap: '12px' }}>
					<div className="flex-fill">
						<Form.Label className="text-muted small d-block">Aprovado</Form.Label>
						<Form.Control
							type="number"
							step={options?.step}
							ref={register}
							name={nomeAprovado}
							placeholder="Aprovado"
							readOnly={!usuarioAdmin}
							style={!usuarioAdmin ? { backgroundColor: '#f5f5f5', cursor: 'default' } : undefined}
						/>
					</div>
					<div className="flex-fill">
						<Form.Label className="text-muted small d-block">Plant.</Form.Label>
						<Form.Control
							type="number"
							step={options?.step}
							ref={register}
							name={nomePlantado}
							placeholder="Plant."
						/>
					</div>
				</div>
			</Form.Group>
		</Form.Row>
	);

	const renderCardTipoMaterial = (
		titulo: string,
		prefixoCampos: string,
		indice: number,
	) => (
		<Col md={12} className="mb-3">
			<div className="border rounded p-3 h-100">
				<Form.Label className="mb-3"><strong>{titulo}</strong></Form.Label>
				{[
					{ label: 'n.º Eventos (entradas)', campo: 'NumeroEventos' },
					{ label: 'n.º Repetições', campo: 'NumeroRepeticoes' },
					{ label: 'n.º Parcelas', campo: 'NumeroParcelas' },
					{ label: 'Tamanho Parcela (m²)', campo: 'TamanhoParcelas', step: 0.01 },
					{ label: 'Área Total (m²)', campo: 'AreaTotal', step: 0.01 },
				].map(({ label, campo, step }) => (
					<div className="mb-3" key={`${prefixoCampos}-${campo}`}>
						<Form.Label>{label}</Form.Label>
						<div className="d-flex flex-column flex-md-row" style={{ gap: '12px' }}>
							<div className="flex-fill">
								<Form.Label className="text-muted small d-block">Aprovado</Form.Label>
								<Form.Control
									type="number"
									step={step}
									ref={register}
									name={`${prefixoCampos}${campo}Aprovado${indice}`}
									placeholder="Aprovado"
									readOnly={!usuarioAdmin}
									style={!usuarioAdmin ? { backgroundColor: '#f5f5f5', cursor: 'default' } : undefined}
								/>
							</div>
							<div className="flex-fill">
								<Form.Label className="text-muted small d-block">Plant.</Form.Label>
								<Form.Control
									type="number"
									step={step}
									ref={register}
									name={`${prefixoCampos}${campo}Plantado${indice}`}
									placeholder="Plant."
								/>
							</div>
						</div>
					</div>
				))}
			</div>
		</Col>
	);


	const salvar = (dados: FormValues) => {
		const valores = getValues();

		console.log('@@ PlantioSojaModal salvar -> valores atuais:', valores);

		const linhasDetalhamento = [] as Array<Record<string, any>>;
		for (let indice = 0; indice < qtdeDetalhamento; indice += 1) {
			const linha = {
				talhao: valores[`detalhamentoTalhao${indice}`],
				experimento: valores[`detalhamentoExperimento${indice}`],
				tipoMaterialGmNumeroEventosAprovado: valores[`detalhamentoTipoMaterialGmNumeroEventosAprovado${indice}`],
				tipoMaterialGmNumeroEventosPlantado: valores[`detalhamentoTipoMaterialGmNumeroEventosPlantado${indice}`],
				tipoMaterialGmNumeroRepeticoesAprovado: valores[`detalhamentoTipoMaterialGmNumeroRepeticoesAprovado${indice}`],
				tipoMaterialGmNumeroRepeticoesPlantado: valores[`detalhamentoTipoMaterialGmNumeroRepeticoesPlantado${indice}`],
				tipoMaterialGmNumeroParcelasAprovado: valores[`detalhamentoTipoMaterialGmNumeroParcelasAprovado${indice}`],
				tipoMaterialGmNumeroParcelasPlantado: valores[`detalhamentoTipoMaterialGmNumeroParcelasPlantado${indice}`],
				tipoMaterialGmTamanhoParcelasAprovado: valores[`detalhamentoTipoMaterialGmTamanhoParcelasAprovado${indice}`],
				tipoMaterialGmTamanhoParcelasPlantado: valores[`detalhamentoTipoMaterialGmTamanhoParcelasPlantado${indice}`],
				tipoMaterialGmAreaTotalAprovado: valores[`detalhamentoTipoMaterialGmAreaTotalAprovado${indice}`],
				tipoMaterialGmAreaTotalPlantado: valores[`detalhamentoTipoMaterialGmAreaTotalPlantado${indice}`],
				tipoMaterialWtOutrasNumeroEventosAprovado: valores[`detalhamentoTipoMaterialWtOutrasNumeroEventosAprovado${indice}`],
				tipoMaterialWtOutrasNumeroEventosPlantado: valores[`detalhamentoTipoMaterialWtOutrasNumeroEventosPlantado${indice}`],
				tipoMaterialWtOutrasNumeroRepeticoesAprovado: valores[`detalhamentoTipoMaterialWtOutrasNumeroRepeticoesAprovado${indice}`],
				tipoMaterialWtOutrasNumeroRepeticoesPlantado: valores[`detalhamentoTipoMaterialWtOutrasNumeroRepeticoesPlantado${indice}`],
				tipoMaterialWtOutrasNumeroParcelasAprovado: valores[`detalhamentoTipoMaterialWtOutrasNumeroParcelasAprovado${indice}`],
				tipoMaterialWtOutrasNumeroParcelasPlantado: valores[`detalhamentoTipoMaterialWtOutrasNumeroParcelasPlantado${indice}`],
				tipoMaterialWtOutrasTamanhoParcelasAprovado: valores[`detalhamentoTipoMaterialWtOutrasTamanhoParcelasAprovado${indice}`],
				tipoMaterialWtOutrasTamanhoParcelasPlantado: valores[`detalhamentoTipoMaterialWtOutrasTamanhoParcelasPlantado${indice}`],
				tipoMaterialWtOutrasAreaTotalAprovado: valores[`detalhamentoTipoMaterialWtOutrasAreaTotalAprovado${indice}`],
				tipoMaterialWtOutrasAreaTotalPlantado: valores[`detalhamentoTipoMaterialWtOutrasAreaTotalPlantado${indice}`],
				numeroEventosAprovado: valores[`detalhamentoNumeroEventosAprovado${indice}`],
				numeroEventosPlantado: valores[`detalhamentoNumeroEventosPlantado${indice}`],
				numeroTratamentosAprovado: valores[`detalhamentoNumeroTratamentosAprovado${indice}`],
				numeroTratamentosPlantado: valores[`detalhamentoNumeroTratamentosPlantado${indice}`],
				numeroRepeticoesAprovado: valores[`detalhamentoNumeroRepeticoesAprovado${indice}`],
				numeroRepeticoesPlantado: valores[`detalhamentoNumeroRepeticoesPlantado${indice}`],
				numeroParcelasAprovado: valores[`detalhamentoNumeroParcelasAprovado${indice}`],
				numeroParcelasPlantado: valores[`detalhamentoNumeroParcelasPlantado${indice}`],
				tamanhoParcelasAprovado: valores[`detalhamentoTamanhoParcelasAprovado${indice}`],
				tamanhoParcelasPlantado: valores[`detalhamentoTamanhoParcelasPlantado${indice}`],
				areaTotalAprovado: valores[`detalhamentoAreaTotalAprovado${indice}`],
				areaTotalPlantado: valores[`detalhamentoAreaTotalPlantado${indice}`],
			};

			if (hasAnyValue(linha)) {
				linhasDetalhamento.push(linha);
			}
		}

		const linhasLimpeza = [] as Array<Record<string, any>>;
		for (let indice = 0; indice < qtdeLimpeza; indice += 1) {
			const linha = {
				nome: valores[`limpezaNome${indice}`],
				dataEntrada: valores[`limpezaDataEntrada${indice}`],
				dataSaida: valores[`limpezaDataSaida${indice}`],
				metodo: formatarMetodoLimpezaParaJson(
					valores[`limpezaMetodo${indice}`],
					valores[`limpezaMetodoOutros${indice}`],
				),
				responsavel: valores[`limpezaResponsavel${indice}`],
			};

			if (hasAnyValue(linha)) {
				linhasLimpeza.push(linha);
			}
		}

		const linhasDetalhamentoFormulario2 = [] as Array<Record<string, any>>;
		for (let indice = 0; indice < qtdeDetalhamentoFormulario2; indice += 1) {
			const linha = {
				talhao: valores[`formulario2DetalhamentoTalhao${indice}`],
				experimento: valores[`formulario2DetalhamentoExperimento${indice}`],
				tipoMaterialGmNumeroEventosAprovado: valores[`formulario2DetalhamentoTipoMaterialGmNumeroEventosAprovado${indice}`],
				tipoMaterialGmNumeroEventosPlantado: valores[`formulario2DetalhamentoTipoMaterialGmNumeroEventosPlantado${indice}`],
				tipoMaterialGmNumeroRepeticoesAprovado: valores[`formulario2DetalhamentoTipoMaterialGmNumeroRepeticoesAprovado${indice}`],
				tipoMaterialGmNumeroRepeticoesPlantado: valores[`formulario2DetalhamentoTipoMaterialGmNumeroRepeticoesPlantado${indice}`],
				tipoMaterialGmNumeroParcelasAprovado: valores[`formulario2DetalhamentoTipoMaterialGmNumeroParcelasAprovado${indice}`],
				tipoMaterialGmNumeroParcelasPlantado: valores[`formulario2DetalhamentoTipoMaterialGmNumeroParcelasPlantado${indice}`],
				tipoMaterialGmTamanhoParcelasAprovado: valores[`formulario2DetalhamentoTipoMaterialGmTamanhoParcelasAprovado${indice}`],
				tipoMaterialGmTamanhoParcelasPlantado: valores[`formulario2DetalhamentoTipoMaterialGmTamanhoParcelasPlantado${indice}`],
				tipoMaterialGmAreaTotalAprovado: valores[`formulario2DetalhamentoTipoMaterialGmAreaTotalAprovado${indice}`],
				tipoMaterialGmAreaTotalPlantado: valores[`formulario2DetalhamentoTipoMaterialGmAreaTotalPlantado${indice}`],
				tipoMaterialWtOutrasNumeroEventosAprovado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasNumeroEventosAprovado${indice}`],
				tipoMaterialWtOutrasNumeroEventosPlantado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasNumeroEventosPlantado${indice}`],
				tipoMaterialWtOutrasNumeroRepeticoesAprovado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasNumeroRepeticoesAprovado${indice}`],
				tipoMaterialWtOutrasNumeroRepeticoesPlantado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasNumeroRepeticoesPlantado${indice}`],
				tipoMaterialWtOutrasNumeroParcelasAprovado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasNumeroParcelasAprovado${indice}`],
				tipoMaterialWtOutrasNumeroParcelasPlantado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasNumeroParcelasPlantado${indice}`],
				tipoMaterialWtOutrasTamanhoParcelasAprovado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasTamanhoParcelasAprovado${indice}`],
				tipoMaterialWtOutrasTamanhoParcelasPlantado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasTamanhoParcelasPlantado${indice}`],
				tipoMaterialWtOutrasAreaTotalAprovado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasAreaTotalAprovado${indice}`],
				tipoMaterialWtOutrasAreaTotalPlantado: valores[`formulario2DetalhamentoTipoMaterialWtOutrasAreaTotalPlantado${indice}`],
				numeroEventosAprovado: valores[`formulario2DetalhamentoNumeroEventosAprovado${indice}`],
				numeroEventosPlantado: valores[`formulario2DetalhamentoNumeroEventosPlantado${indice}`],
				numeroTratamentosAprovado: valores[`formulario2DetalhamentoNumeroTratamentosAprovado${indice}`],
				numeroTratamentosPlantado: valores[`formulario2DetalhamentoNumeroTratamentosPlantado${indice}`],
				numeroRepeticoesAprovado: valores[`formulario2DetalhamentoNumeroRepeticoesAprovado${indice}`],
				numeroRepeticoesPlantado: valores[`formulario2DetalhamentoNumeroRepeticoesPlantado${indice}`],
				numeroParcelasAprovado: valores[`formulario2DetalhamentoNumeroParcelasAprovado${indice}`],
				numeroParcelasPlantado: valores[`formulario2DetalhamentoNumeroParcelasPlantado${indice}`],
				tamanhoParcelasAprovado: valores[`formulario2DetalhamentoTamanhoParcelasAprovado${indice}`],
				tamanhoParcelasPlantado: valores[`formulario2DetalhamentoTamanhoParcelasPlantado${indice}`],
				areaTotalAprovado: valores[`formulario2DetalhamentoAreaTotalAprovado${indice}`],
				areaTotalPlantado: valores[`formulario2DetalhamentoAreaTotalPlantado${indice}`],
			};

			if (hasAnyValue(linha)) {
				linhasDetalhamentoFormulario2.push(linha);
			}
		}

		const linhasLimpezaFormulario2 = [] as Array<Record<string, any>>;
		for (let indice = 0; indice < qtdeLimpezaFormulario2; indice += 1) {
			const linha = {
				nome: valores[`formulario2LimpezaNome${indice}`],
				dataEntrada: valores[`formulario2LimpezaDataEntrada${indice}`],
				dataSaida: valores[`formulario2LimpezaDataSaida${indice}`],
				metodo: formatarMetodoLimpezaParaJson(
					valores[`formulario2LimpezaMetodo${indice}`],
					valores[`formulario2LimpezaMetodoOutros${indice}`],
				),
				responsavel: valores[`formulario2LimpezaResponsavel${indice}`],
			};

			if (hasAnyValue(linha)) {
				linhasLimpezaFormulario2.push(linha);
			}
		}

		const dadosEnvio: Record<string, any> = {
			...dados,
			tipoPlantioAprovadoMecanico: !!dados.tipoPlantioAprovadoMecanico,
			tipoPlantioAprovadoManual: !!dados.tipoPlantioAprovadoManual,
			tipoPlantioRealizadoMecanico: !!dados.tipoPlantioRealizadoMecanico,
			tipoPlantioRealizadoManual: !!dados.tipoPlantioRealizadoManual,
			formulario2TipoPlantioAprovadoMecanico: !!dados.formulario2TipoPlantioAprovadoMecanico,
			formulario2TipoPlantioAprovadoManual: !!dados.formulario2TipoPlantioAprovadoManual,
			formulario2TipoPlantioRealizadoMecanico: !!dados.formulario2TipoPlantioRealizadoMecanico,
			formulario2TipoPlantioRealizadoManual: !!dados.formulario2TipoPlantioRealizadoManual,
			checklistPlacaBiosseguranca: !!dados.checklistPlacaBiosseguranca,
			checklistEstacasDelimitacao: !!dados.checklistEstacasDelimitacao,
			checklistComunicadoCroqui: !!dados.checklistComunicadoCroqui,
			checklistEnvelopesDestruidos: !!dados.checklistEnvelopesDestruidos,
			checklistRoupasEquipamentosLimpos: !!dados.checklistRoupasEquipamentosLimpos,
			checklistPlantio2PlacaBiosseguranca: !!dados.checklistPlantio2PlacaBiosseguranca,
			checklistPlantio2EstacasDelimitacao: !!dados.checklistPlantio2EstacasDelimitacao,
			checklistPlantio2ComunicadoCroqui: !!dados.checklistPlantio2ComunicadoCroqui,
			checklistPlantio2EnvelopesDestruidos: !!dados.checklistPlantio2EnvelopesDestruidos,
			checklistPlantio2RoupasEquipamentosLimpos: !!dados.checklistPlantio2RoupasEquipamentosLimpos,
			assinaturaEnvolvidosBase64: assinaturaEnvolvidos || null,
			formulario2AssinaturaEnvolvidosBase64: assinaturaFormulario2Envolvidos || null,
			nomesEnvolvidosAdicionaisJson: stringifyNomesEnvolvidos(nomesAdicionais),
			formulario2NomesEnvolvidosAdicionaisJson: stringifyNomesEnvolvidos(nomesAdicionaisForm2),
			detalhamentoPlantioJson: linhasDetalhamento.length ? JSON.stringify(linhasDetalhamento) : null,
			limpezaMaquinariosJson: linhasLimpeza.length ? JSON.stringify(linhasLimpeza) : null,
			formulario2DetalhamentoPlantioJson: linhasDetalhamentoFormulario2.length ? JSON.stringify(linhasDetalhamentoFormulario2) : null,
			formulario2LimpezaMaquinariosJson: linhasLimpezaFormulario2.length ? JSON.stringify(linhasLimpezaFormulario2) : null,
		};

		for (let indice = 0; indice < qtdeLimpeza; indice += 1) {
			delete dadosEnvio[`limpezaMetodoOutros${indice}`];
		}
		for (let indice = 0; indice < qtdeLimpezaFormulario2; indice += 1) {
			delete dadosEnvio[`formulario2LimpezaMetodoOutros${indice}`];
		}

		onSubmit(dadosEnvio as CadernoLPMAPlantioSoja);

		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.PLANTIOS_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(salvar)} autoComplete="off">
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label><h2>Formulário - Registro de atividades/informações do Plantio</h2></Form.Label>
						<p className="text-muted">Este formulário deve ser preenchido por ocasião do plantio da LPMA.</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
							{abasPlantioSoja.map((aba) => (
								<Button
									key={aba.valor}
									title={aba.label}
									contained={abaPlantioSoja === aba.valor}
									className={abaPlantioSoja === aba.valor ? 'active' : ''}
									type="button"
									onClick={() => handleMudarAbaPlantioSoja(aba.valor)}
								/>
							))}
						</div>
					</Form.Group>
				</Form.Row>

				{/* Aba 1 - Colaboradores */}
				{abaPlantioSoja === 1 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label>Identificação do plantio</Form.Label>
								<Form.Control
									ref={register}
									name="identificacaoPlantio"
									placeholder="Ex.: Plantio 1"
									maxLength={100}
								/>
							</Form.Group>
						</Form.Row>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={6}>
								<Form.Label>Data inicial do plantio</Form.Label>
								<Controller
									control={control}
									name="dataInicioPlantio"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={6}>
								<Form.Label>Data final do plantio</Form.Label>
								<Controller
									control={control}
									name="dataTerminoPlantio"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
						</Form.Row>
						<AssinaturaComNomesAdicionais
							label="assinatura"
							assinaturaUsuario={assinaturaUsuario}
							assinaturaBase64={assinaturaEnvolvidos}
							onAssinaturaBase64Change={setAssinaturaEnvolvidos}
							nomesAdicionais={nomesAdicionais}
							onNomesAdicionaisChange={setNomesAdicionais}
							mostrarAssinatura={mostrarAssinatura}
							onMostrarAssinaturaChange={setMostrarAssinatura}
							assinaturasDisponiveis={assinaturasDisponiveis}
						/>
					</>
				)}


				{/* Aba 2 - Instalação do ensaio e localização */}
				{abaPlantioSoja === 2 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={4}>
								<Form.Label>Talhão</Form.Label>
								<Form.Control ref={register} name="talhao" placeholder="Informe o talhão" />
							</Form.Group>
							<Form.Group as={Col} md={4}>
								<Form.Label>Tipo de plantio APROVADO</Form.Label>
								<div className="d-flex" style={{ gap: '12px' }}>
									<Form.Check
										type="checkbox"
										id="tipoPlantioAprovadoMecanico"
										label="Mecânico"
										checked={!!tipoPlantioAprovadoMecanico}
										onChange={() => setValue('tipoPlantioAprovadoMecanico', !tipoPlantioAprovadoMecanico, { shouldDirty: true })}
									/>
									<Form.Check
										type="checkbox"
										id="tipoPlantioAprovadoManual"
										label="Manual"
										checked={!!tipoPlantioAprovadoManual}
										onChange={() => setValue('tipoPlantioAprovadoManual', !tipoPlantioAprovadoManual, { shouldDirty: true })}
									/>
								</div>
							</Form.Group>
							<Form.Group as={Col} md={4}>
								<Form.Label>Tipo de plantio REALIZADO</Form.Label>
								<div className="d-flex" style={{ gap: '12px' }}>
									<Form.Check
										type="checkbox"
										id="tipoPlantioRealizadoMecanico"
										label="Mecânico"
										checked={!!tipoPlantioRealizadoMecanico}
										onChange={() => setValue('tipoPlantioRealizadoMecanico', !tipoPlantioRealizadoMecanico, { shouldDirty: true })}
									/>
									<Form.Check
										type="checkbox"
										id="tipoPlantioRealizadoManual"
										label="Manual"
										checked={!!tipoPlantioRealizadoManual}
										onChange={() => setValue('tipoPlantioRealizadoManual', !tipoPlantioRealizadoManual, { shouldDirty: true })}
									/>
								</div>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label><strong>Informações técnicas (aprovação x plantio)</strong></Form.Label>
							</Form.Group>
						</Form.Row>

						{Array.from({ length: qtdeDetalhamento }).map((_, index) => (
							<div key={`detalhamento-${index}`} className="border rounded p-3 mb-3">
								<Form.Row className="row-custom">
									<Form.Group as={Col} md={12}>
										<Form.Label>Talhão</Form.Label>
										<Form.Control
											ref={register}
											name={`detalhamentoTalhao${index}`}
											placeholder="Identifique o talhão"
										/>
									</Form.Group>
								</Form.Row>
								<Form.Row className="row-custom">
									<Form.Group as={Col} md={12}>
										<Form.Label>Experimento e/ou Ensaio</Form.Label>
										<Form.Control
											ref={register}
											name={`detalhamentoExperimento${index}`}
											placeholder="Informe o experimento/ensaio"
										/>
									</Form.Group>
								</Form.Row>
								<Form.Row className="row-custom">
									{renderCardTipoMaterial(
										'GM',
										'detalhamentoTipoMaterialGm',
										index,
									)}
									{renderCardTipoMaterial(
										'WT/outras (especificar)',
										'detalhamentoTipoMaterialWtOutras',
										index,
									)}
								</Form.Row>

								{renderCampoComparativo(
									'n.º Eventos (entradas)',
									`detalhamentoNumeroEventosAprovado${index}`,
									`detalhamentoNumeroEventosPlantado${index}`,
								)}
								{renderCampoComparativo(
									'n.º Tratamentos',
									`detalhamentoNumeroTratamentosAprovado${index}`,
									`detalhamentoNumeroTratamentosPlantado${index}`,
								)}
								{renderCampoComparativo(
									'n.º Repetições',
									`detalhamentoNumeroRepeticoesAprovado${index}`,
									`detalhamentoNumeroRepeticoesPlantado${index}`,
								)}
								{renderCampoComparativo(
									'n.º Parcelas',
									`detalhamentoNumeroParcelasAprovado${index}`,
									`detalhamentoNumeroParcelasPlantado${index}`,
								)}
								{renderCampoComparativo(
									'Tamanho Parcela (m²)',
									`detalhamentoTamanhoParcelasAprovado${index}`,
									`detalhamentoTamanhoParcelasPlantado${index}`,
									{ step: 0.01 },
								)}
								{renderCampoComparativo(
									'Área Total (m²)',
									`detalhamentoAreaTotalAprovado${index}`,
									`detalhamentoAreaTotalPlantado${index}`,
									{ step: 0.01 },
								)}
							</div>
						))}

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label><strong>Resumo/Total geral LPMA (m²)</strong></Form.Label>
							</Form.Group>
						</Form.Row>
						{renderCampoComparativo('OGM (m²)', 'resumoOgmAprovadoM2', 'resumoOgmPlantadoM2', { step: 0.01 })}
						{renderCampoComparativo(
							'Comercial/WT (m²)',
							'resumoComercialAprovadoM2',
							'resumoComercialPlantadoM2',
							{ step: 0.01 },
						)}
						{renderCampoComparativo(
							'OGM + Comercial/WT (m²)',
							'resumoTotalAprovadoM2',
							'resumoTotalPlantadoM2',
							{ step: 0.01 },
						)}

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={3}>
								<Form.Label>Distância de isolamento mínima exigida LPMA (m)</Form.Label>
								<Form.Control type="number" step="0.01" ref={register} name="distanciaIsolamentoExigida" placeholder="Informe a distância" />
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Label>Distância de isolamento mantida LPMA (m)</Form.Label>
								<Form.Control type="number" step="0.01" ref={register} name="distanciaIsolamentoMantida" placeholder="Informe a distância" />
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Label>Largura da bordadura mínima exigida LPMA (m)</Form.Label>
								<Form.Control type="number" step="0.01" ref={register} name="larguraBordaduraExigida" placeholder="Informe a largura" />
							</Form.Group>
							<Form.Group as={Col} md={3}>
								<Form.Label>Largura da bordadura plantada LPMA (m)</Form.Label>
								<Form.Control type="number" step="0.01" ref={register} name="larguraBordaduraPlantada" placeholder="Informe a largura" />
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label>Material plantado na bordadura</Form.Label>
								<Form.Control ref={register} name="materialPlantadoBordadura" placeholder="Descreva o material plantado na bordadura" />
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<small className="text-muted">Formato: graus, minutos e segundos – Ex.: P1: 15° 31' 10.11" - W 54° 23' 54.17"</small>
							</Form.Group>
						</Form.Row>

						{Array.from({ length: Math.ceil(qtdeCoordenadas / 2) }).map((_, rowIdx) => {
							const left = coordinateFields[rowIdx * 2];
							const right = coordinateFields[rowIdx * 2 + 1];
							const mostrarDireita = rowIdx * 2 + 1 < qtdeCoordenadas;
							return (
								<Row className="row-custom" key={`coord-row-${rowIdx}`}>
									<CoordenadaDmsField
										control={control}
										name={left.name as string}
										label={left.label}
									/>
									{mostrarDireita && right && (
										<CoordenadaDmsField
											control={control}
											name={right.name as string}
											label={right.label}
										/>
									)}
								</Row>
							);
						})}

						<div className="button-position-caderno mb-3" style={{ justifyContent: 'flex-end', gap: '8px' }}>
							<Button title="+ Coordenada" contained={false} type="button" onClick={adicionarCoordenada} />
							<Button title="- Coordenada" contained={false} type="button" onClick={removerCoordenada} />
						</div>
					</>
				)}

				{/* Aba 4 - Limpeza */}
				{abaPlantioSoja === 4 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<div className="table-responsive">
									<table className="table table-bordered table-sm">
										<thead className="table-light">
											<tr>
												<th>Nome e código</th>
												<th>Data limpeza - Antes (entrada)</th>
												<th>Data limpeza - Após (saída)</th>
												<th>Método de limpeza</th>
												<th>Responsável</th>
											</tr>
										</thead>
										<tbody>
											{Array.from({ length: qtdeLimpeza }).map((_, index) => {
												const metodoSelecionado = watch(`limpezaMetodo${index}` as any);
												return (
												<tr key={`limpeza-${index}`}>
													<td>
														<Form.Control ref={register} name={`limpezaNome${index}`} placeholder="Nome / Código" />
													</td>
													<td>
														<Controller
															control={control}
															name={`limpezaDataEntrada${index}`}
															render={({ onChange, value }) => (
																<DatePicker
																	selected={value || null}
																	onChange={onChange}
																	className="input-custom-to-date"
																	locale="pt-BR"
																	dateFormat="dd/MM/yyyy"
																	placeholderText="Selecionar"
																/>
															)}
														/>
													</td>
													<td>
														<Controller
															control={control}
															name={`limpezaDataSaida${index}`}
															render={({ onChange, value }) => (
																<DatePicker
																	selected={value || null}
																	onChange={onChange}
																	className="input-custom-to-date"
																	locale="pt-BR"
																	dateFormat="dd/MM/yyyy"
																	placeholderText="Selecionar"
																/>
															)}
														/>
													</td>
													<td>
														<MetodoLimpezaSelect
															register={register}
															name={`limpezaMetodo${index}`}
															nameOutros={`limpezaMetodoOutros${index}`}
															metodoSelecionado={metodoSelecionado}
														/>
													</td>
													<td>
														<Form.Control ref={register} name={`limpezaResponsavel${index}`} placeholder="Responsável" />
													</td>
												</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</Form.Group>
						</Form.Row>

						<div className="button-position-caderno mb-3" style={{ justifyContent: 'flex-end', gap: '8px' }}>
							<Button title="+ Maquinário" contained={false} type="button" onClick={adicionarLinhaLimpeza} />
							<Button title="- Maquinário" contained={false} type="button" onClick={removerLinhaLimpeza} />
						</div>
					</>
				)}

				{/* Aba 5 - Comunicação */}
				{abaPlantioSoja === 5 && (
					<>
						<Row className="row-custom">
							<Col md={6}>
								<Form.Group>
									<Form.Label>Comunicação à CIBio sobre o início do plantio – Data:</Form.Label>
									<Controller
										control={control}
										name="dataComunicacaoInicioPlantio"
										render={({ onChange, value }) => (
											<DatePicker
												selected={value || null}
												onChange={onChange}
												className="input-custom-to-date"
												locale="pt-BR"
												dateFormat="dd/MM/yyyy"
												placeholderText="Selecionar a data"
											/>
										)}
									/>
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group>
									<Form.Label>Comunicação à CIBio sobre o término do plantio – Data:</Form.Label>
									<Controller
										control={control}
										name="dataComunicacaoTerminoPlantio"
										render={({ onChange, value }) => (
											<DatePicker
												selected={value || null}
												onChange={onChange}
												className="input-custom-to-date"
												locale="pt-BR"
												dateFormat="dd/MM/yyyy"
												placeholderText="Selecionar a data"
											/>
										)}
									/>
								</Form.Group>
							</Col>
						</Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<small className="text-muted">
									<strong>IMPORTANTE:</strong> Anexar a cópia do(s) e-mail(s) informando a CIBio sobre o início/término do plantio na pasta de campo ABA COMUNICADOS INTERNOS, assim como a cópia do(s) ofício(s) da CIBio para CTNBio/MAPA com as mesmas informações na pasta de campo ABA COMUNICADOS EXTERNOS.
								</small>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label>Observações e informações adicionais</Form.Label>
								<Form.Control
									as="textarea"
									rows={3}
									ref={register}
									name="observacoesInformacoesAdicionais"
									placeholder="Descreva observações relevantes"
								/>
							</Form.Group>
						</Form.Row>
					</>
				)}

				{/* Aba 3 - Questionário */}
				{abaPlantioSoja === 3 && (
					<>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<Form.Label>
									<h2>Checklist - Plantio</h2>
								</Form.Label>
								<p>As perguntas abaixo são pontos de checagem visando o atendimento dos requisitos de biossegurança:</p>
							</Form.Group>
						</Form.Row>

						<Form.Row className="row-custom mt-3">
							<Form.Group as={Col} md={12}>
								<Form.Check
									type="checkbox"
									ref={register}
									name="checklistPlacaBiosseguranca"
									label="a. A placa de biossegurança foi preenchida e instalada nas quadras/talhões ocupados pela LPMA?"
								/>
							</Form.Group>
						</Form.Row>
						<ComentarioExpandivel
							name="checklistPlacaBiossegurancaObservacao"
							register={register}
							valorInicial={initialData?.checklistPlacaBiossegurancaObservacao}
						/>

						<Form.Row className="row-custom mt-3">
							<Form.Group as={Col} md={12}>
								<Form.Check
									type="checkbox"
									ref={register}
									name="checklistEstacasDelimitacao"
									label="b. As estacas de delimitação foram colocadas nas extremidades da LPMA?"
								/>
							</Form.Group>
						</Form.Row>
						<ComentarioExpandivel
							name="checklistEstacasDelimitacaoObservacao"
							register={register}
							valorInicial={initialData?.checklistEstacasDelimitacaoObservacao}
						/>

						<Form.Row className="row-custom mt-3">
							<Form.Group as={Col} md={12}>
								<Form.Check
									type="checkbox"
									ref={register}
									name="checklistComunicadoCroqui"
									label="c. O comunicado de plantio com o croqui, mapa e coordenadas geográficas atualizadas foi encaminhado para CIBio?"
								/>
							</Form.Group>
						</Form.Row>
						<ComentarioExpandivel
							name="checklistComunicadoCroquiObservacao"
							register={register}
							valorInicial={initialData?.checklistComunicadoCroquiObservacao}
						/>

						<Form.Row className="row-custom mt-3">
							<Form.Group as={Col} md={12}>
								<Form.Check
									type="checkbox"
									ref={register}
									name="checklistEnvelopesDestruidos"
									label="d. Os envelopes de sementes e embalagens remanescentes do plantio da LPMA foram devidamente destruídos/descartados, e o registro foi realizado na página de destruição e descarte?"
								/>
							</Form.Group>
						</Form.Row>
						<ComentarioExpandivel
							name="checklistEnvelopesDestruidosObservacao"
							register={register}
							valorInicial={initialData?.checklistEnvelopesDestruidosObservacao}
						/>

						<Form.Row className="row-custom mt-3">
							<Form.Group as={Col} md={12}>
								<Form.Check
									type="checkbox"
									ref={register}
									name="checklistRoupasEquipamentosLimpos"
									label="e. Roupas, calçados assim como maquinários, equipamentos e utensílios utilizados pela equipe foram limpos e inspecionados ANTES de entrar e ANTES de sair da área da LPMA?"
								/>
							</Form.Group>
						</Form.Row>
						<ComentarioExpandivel
							name="checklistRoupasEquipamentosLimposObservacao"
							register={register}
							valorInicial={initialData?.checklistRoupasEquipamentosLimposObservacao}
						/>
					</>
				)}

				{exibirFormulario2Plantio && (
					<>
						<hr className="my-4" />

						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
									{abasPlantioSojaFormulario2.map((aba) => (
										<Button
											key={`formulario2-aba-${aba.valor}`}
											title={aba.label}
											contained={abaPlantioSojaFormulario2 === aba.valor}
											className={abaPlantioSojaFormulario2 === aba.valor ? 'active' : ''}
											type="button"
											onClick={() => handleMudarAbaPlantioSojaFormulario2(aba.valor)}
										/>
									))}
								</div>
							</Form.Group>
						</Form.Row>

						<hr className="my-3" />

						{/* Formulário 2 - Aba 1 */}
						{abaPlantioSojaFormulario2 === 1 && (
							<>
								<Form.Row className="row-custom">
									<Form.Group as={Col} md={6}>
										<Form.Label>Data inicial do plantio</Form.Label>
										<Controller
											control={control}
											name="formulario2DataInicioPlantio"
											render={({ onChange, value }) => (
												<DatePicker
													selected={value || null}
													onChange={onChange}
													className="input-custom-to-date"
													locale="pt-BR"
													dateFormat="dd/MM/yyyy"
													placeholderText="Selecionar a data"
												/>
											)}
										/>
									</Form.Group>
									<Form.Group as={Col} md={6}>
										<Form.Label>Data final do plantio</Form.Label>
										<Controller
											control={control}
											name="formulario2DataTerminoPlantio"
											render={({ onChange, value }) => (
												<DatePicker
													selected={value || null}
													onChange={onChange}
													className="input-custom-to-date"
													locale="pt-BR"
													dateFormat="dd/MM/yyyy"
													placeholderText="Selecionar a data"
												/>
											)}
										/>
									</Form.Group>
								</Form.Row>
								<AssinaturaComNomesAdicionais
									label="assinatura"
									assinaturaUsuario={assinaturaUsuario}
									assinaturaBase64={assinaturaFormulario2Envolvidos}
									onAssinaturaBase64Change={setAssinaturaFormulario2Envolvidos}
									nomesAdicionais={nomesAdicionaisForm2}
									onNomesAdicionaisChange={setNomesAdicionaisForm2}
									mostrarAssinatura={mostrarAssinaturaFormulario2}
									onMostrarAssinaturaChange={setMostrarAssinaturaFormulario2}
									assinaturasDisponiveis={assinaturasDisponiveis}
								/>
							</>
						)}

						{/* Formulário 2 - Aba 2 */}
						{abaPlantioSojaFormulario2 === 2 && (
							<>
								<Form.Row className="row-custom">
									<Form.Group as={Col} md={4}>
										<Form.Label>Talhão</Form.Label>
										<Form.Control ref={register} name="formulario2Talhao" placeholder="Informe o talhão" />
									</Form.Group>
									<Form.Group as={Col} md={4}>
										<Form.Label>Tipo de plantio APROVADO</Form.Label>
										<div className="d-flex" style={{ gap: '12px' }}>
											<Form.Check
												type="checkbox"
												id="formulario2TipoPlantioAprovadoMecanico"
												label="Mecânico"
												checked={!!formulario2TipoPlantioAprovadoMecanico}
												onChange={() => setValue('formulario2TipoPlantioAprovadoMecanico', !formulario2TipoPlantioAprovadoMecanico, { shouldDirty: true })}
											/>
											<Form.Check
												type="checkbox"
												id="formulario2TipoPlantioAprovadoManual"
												label="Manual"
												checked={!!formulario2TipoPlantioAprovadoManual}
												onChange={() => setValue('formulario2TipoPlantioAprovadoManual', !formulario2TipoPlantioAprovadoManual, { shouldDirty: true })}
											/>
										</div>
									</Form.Group>
									<Form.Group as={Col} md={4}>
										<Form.Label>Tipo de plantio REALIZADO</Form.Label>
										<div className="d-flex" style={{ gap: '12px' }}>
											<Form.Check
												type="checkbox"
												id="formulario2TipoPlantioRealizadoMecanico"
												label="Mecânico"
												checked={!!formulario2TipoPlantioRealizadoMecanico}
												onChange={() => setValue('formulario2TipoPlantioRealizadoMecanico', !formulario2TipoPlantioRealizadoMecanico, { shouldDirty: true })}
											/>
											<Form.Check
												type="checkbox"
												id="formulario2TipoPlantioRealizadoManual"
												label="Manual"
												checked={!!formulario2TipoPlantioRealizadoManual}
												onChange={() => setValue('formulario2TipoPlantioRealizadoManual', !formulario2TipoPlantioRealizadoManual, { shouldDirty: true })}
											/>
										</div>
									</Form.Group>
								</Form.Row>

								<Form.Row className="row-custom">
									<Form.Group as={Col} md={12}>
										<Form.Label><strong>Informações técnicas (aprovação x plantio)</strong></Form.Label>
									</Form.Group>
								</Form.Row>

								{Array.from({ length: qtdeDetalhamentoFormulario2 }).map((_, index) => (
									<div key={`formulario2-detalhamento-${index}`} className="border rounded p-3 mb-3">
										<Form.Row className="row-custom">
											<Form.Group as={Col} md={12}>
												<Form.Label>Talhão</Form.Label>
												<Form.Control
													ref={register}
													name={`formulario2DetalhamentoTalhao${index}`}
													placeholder="Identifique o talhão"
												/>
											</Form.Group>
										</Form.Row>
										<Form.Row className="row-custom">
											<Form.Group as={Col} md={12}>
												<Form.Label>Experimento e/ou Ensaio</Form.Label>
												<Form.Control
													ref={register}
													name={`formulario2DetalhamentoExperimento${index}`}
													placeholder="Informe o experimento/ensaio"
												/>
											</Form.Group>
										</Form.Row>
										<Form.Row className="row-custom">
											{renderCardTipoMaterial(
												'GM',
												'formulario2DetalhamentoTipoMaterialGm',
												index,
											)}
											{renderCardTipoMaterial(
												'WT/outras (especificar)',
												'formulario2DetalhamentoTipoMaterialWtOutras',
												index,
											)}
										</Form.Row>

										{renderCampoComparativo(
											'n.º Eventos (entradas)',
											`formulario2DetalhamentoNumeroEventosAprovado${index}`,
											`formulario2DetalhamentoNumeroEventosPlantado${index}`,
										)}
										{renderCampoComparativo(
											'n.º Tratamentos',
											`formulario2DetalhamentoNumeroTratamentosAprovado${index}`,
											`formulario2DetalhamentoNumeroTratamentosPlantado${index}`,
										)}
										{renderCampoComparativo(
											'n.º Repetições',
											`formulario2DetalhamentoNumeroRepeticoesAprovado${index}`,
											`formulario2DetalhamentoNumeroRepeticoesPlantado${index}`,
										)}
										{renderCampoComparativo(
											'n.º Parcelas',
											`formulario2DetalhamentoNumeroParcelasAprovado${index}`,
											`formulario2DetalhamentoNumeroParcelasPlantado${index}`,
										)}
										{renderCampoComparativo(
											'Tamanho Parcela (m²)',
											`formulario2DetalhamentoTamanhoParcelasAprovado${index}`,
											`formulario2DetalhamentoTamanhoParcelasPlantado${index}`,
											{ step: 0.01 },
										)}
										{renderCampoComparativo(
											'Área Total (m²)',
											`formulario2DetalhamentoAreaTotalAprovado${index}`,
											`formulario2DetalhamentoAreaTotalPlantado${index}`,
											{ step: 0.01 },
										)}
									</div>
								))}

								<Form.Row className="row-custom">
									<Form.Group as={Col} md={12}>
										<Form.Label><strong>Resumo/Total geral LPMA (m²)</strong></Form.Label>
									</Form.Group>
								</Form.Row>
								{renderCampoComparativo(
									'OGM (m²)',
									'formulario2ResumoOgmAprovadoM2',
									'formulario2ResumoOgmPlantadoM2',
									{ step: 0.01 },
								)}
								{renderCampoComparativo(
									'Comercial/WT (m²)',
									'formulario2ResumoComercialAprovadoM2',
									'formulario2ResumoComercialPlantadoM2',
									{ step: 0.01 },
								)}
								{renderCampoComparativo(
									'OGM + Comercial/WT (m²)',
									'formulario2ResumoTotalAprovadoM2',
									'formulario2ResumoTotalPlantadoM2',
									{ step: 0.01 },
								)}

								<Form.Row className="row-custom">
									<Form.Group as={Col} md={3}>
										<Form.Label>Distância de isolamento mínima exigida LPMA (m)</Form.Label>
										<Form.Control type="number" step="0.01" ref={register} name="formulario2DistanciaIsolamentoExigida" placeholder="Informe a distância" />
									</Form.Group>
									<Form.Group as={Col} md={3}>
										<Form.Label>Distância de isolamento mantida LPMA (m)</Form.Label>
										<Form.Control type="number" step="0.01" ref={register} name="formulario2DistanciaIsolamentoMantida" placeholder="Informe a distância" />
									</Form.Group>
									<Form.Group as={Col} md={3}>
										<Form.Label>Largura da bordadura mínima exigida LPMA (m)</Form.Label>
										<Form.Control type="number" step="0.01" ref={register} name="formulario2LarguraBordaduraExigida" placeholder="Informe a largura" />
									</Form.Group>
									<Form.Group as={Col} md={3}>
										<Form.Label>Largura da bordadura plantada LPMA (m)</Form.Label>
										<Form.Control type="number" step="0.01" ref={register} name="formulario2LarguraBordaduraPlantada" placeholder="Informe a largura" />
									</Form.Group>
								</Form.Row>

								<Form.Row className="row-custom">
									<Form.Group as={Col} md={12}>
										<Form.Label>Material plantado na bordadura</Form.Label>
										<Form.Control ref={register} name="formulario2MaterialPlantadoBordadura" placeholder="Descreva o material plantado na bordadura" />
									</Form.Group>
								</Form.Row>

								<Form.Row className="row-custom">
									<Form.Group as={Col} md={12}>
										<small className="text-muted">Formato: graus, minutos e segundos – Ex.: P1: 15° 31' 10.11" - W 54° 23' 54.17"</small>
									</Form.Group>
								</Form.Row>

								{Array.from({ length: Math.ceil(qtdeCoordenadasFormulario2 / 2) }).map((_, rowIdx) => {
									const left = coordinateFieldsFormulario2[rowIdx * 2];
									const right = coordinateFieldsFormulario2[rowIdx * 2 + 1];
									const mostrarDireita = rowIdx * 2 + 1 < qtdeCoordenadasFormulario2;
									return (
										<Row className="row-custom" key={`formulario2-coord-row-${rowIdx}`}>
											<CoordenadaDmsField
												control={control}
												name={left.name as string}
												label={left.label}
											/>
											{mostrarDireita && right && (
												<CoordenadaDmsField
													control={control}
													name={right.name as string}
													label={right.label}
												/>
											)}
										</Row>
									);
								})}

								<div className="button-position-caderno mb-3" style={{ justifyContent: 'flex-end', gap: '8px' }}>
									<Button title="+ Coordenada" contained={false} type="button" onClick={adicionarCoordenadaFormulario2} />
									<Button title="- Coordenada" contained={false} type="button" onClick={removerCoordenadaFormulario2} />
								</div>
							</>
						)}

						{/* Formulário 2 - Aba 4 */}
						{abaPlantioSojaFormulario2 === 4 && (
							<>
								<Form.Row className="row-custom">
									<Form.Group as={Col} md={12}>
										<div className="table-responsive">
											<table className="table table-bordered table-sm">
												<thead className="table-light">
													<tr>
														<th>Nome e código</th>
														<th>Data limpeza - Antes (entrada)</th>
														<th>Data limpeza - Após (saída)</th>
														<th>Método de limpeza</th>
														<th>Responsável</th>
													</tr>
												</thead>
												<tbody>
													{Array.from({ length: qtdeLimpezaFormulario2 }).map((_, index) => {
														const metodoSelecionado = watch(`formulario2LimpezaMetodo${index}` as any);
														return (
														<tr key={`formulario2-limpeza-${index}`}>
															<td>
																<Form.Control ref={register} name={`formulario2LimpezaNome${index}`} placeholder="Nome / Código" />
															</td>
															<td>
																<Controller
																	control={control}
																	name={`formulario2LimpezaDataEntrada${index}`}
																	render={({ onChange, value }) => (
																		<DatePicker
																			selected={value || null}
																			onChange={onChange}
																			className="input-custom-to-date"
																			locale="pt-BR"
																			dateFormat="dd/MM/yyyy"
																			placeholderText="Selecionar"
																		/>
																	)}
																/>
															</td>
															<td>
																<Controller
																	control={control}
																	name={`formulario2LimpezaDataSaida${index}`}
																	render={({ onChange, value }) => (
																		<DatePicker
																			selected={value || null}
																			onChange={onChange}
																			className="input-custom-to-date"
																			locale="pt-BR"
																			dateFormat="dd/MM/yyyy"
																			placeholderText="Selecionar"
																		/>
																	)}
																/>
															</td>
															<td>
																<MetodoLimpezaSelect
																	register={register}
																	name={`formulario2LimpezaMetodo${index}`}
																	nameOutros={`formulario2LimpezaMetodoOutros${index}`}
																	metodoSelecionado={metodoSelecionado}
																/>
															</td>
															<td>
																<Form.Control ref={register} name={`formulario2LimpezaResponsavel${index}`} placeholder="Responsável" />
															</td>
														</tr>
														);
													})}
												</tbody>
											</table>
										</div>
									</Form.Group>
								</Form.Row>

								<div className="button-position-caderno mb-3" style={{ justifyContent: 'flex-end', gap: '8px' }}>
									<Button title="+ Maquinário" contained={false} type="button" onClick={adicionarLinhaLimpezaFormulario2} />
									<Button title="- Maquinário" contained={false} type="button" onClick={removerLinhaLimpezaFormulario2} />
								</div>
							</>
						)}

						{/* Formulário 2 - Aba 5 */}
						{abaPlantioSojaFormulario2 === 5 && (
							<>
								<Row className="row-custom">
									<Col md={6}>
										<Form.Group>
											<Form.Label>Comunicação à CIBio sobre o início do plantio – Data:</Form.Label>
											<Controller
												control={control}
												name="formulario2DataComunicacaoInicioPlantio"
												render={({ onChange, value }) => (
													<DatePicker
														selected={value || null}
														onChange={onChange}
														className="input-custom-to-date"
														locale="pt-BR"
														dateFormat="dd/MM/yyyy"
														placeholderText="Selecionar a data"
													/>
												)}
											/>
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group>
											<Form.Label>Comunicação à CIBio sobre o término do plantio – Data:</Form.Label>
											<Controller
												control={control}
												name="formulario2DataComunicacaoTerminoPlantio"
												render={({ onChange, value }) => (
													<DatePicker
														selected={value || null}
														onChange={onChange}
														className="input-custom-to-date"
														locale="pt-BR"
														dateFormat="dd/MM/yyyy"
														placeholderText="Selecionar a data"
													/>
												)}
											/>
										</Form.Group>
									</Col>
								</Row>

								<Form.Row className="row-custom">
									<Form.Group as={Col} md={12}>
										<small className="text-muted">
											<strong>IMPORTANTE:</strong> Anexar a cópia do(s) e-mail(s) informando a CIBio sobre o início/término do plantio na pasta de campo ABA COMUNICADOS INTERNOS, assim como a cópia do(s) ofício(s) da CIBio para CTNBio/MAPA com as mesmas informações na pasta de campo ABA COMUNICADOS EXTERNOS.
										</small>
									</Form.Group>
								</Form.Row>

								<Form.Row className="row-custom">
									<Form.Group as={Col} md={12}>
										<Form.Label>Observações e informações adicionais</Form.Label>
										<Form.Control
											as="textarea"
											rows={3}
											ref={register}
											name="formulario2ObservacoesInformacoesAdicionais"
											placeholder="Descreva observações relevantes"
										/>
									</Form.Group>
								</Form.Row>
							</>
						)}
						<hr className="my-4" />
					</>
				)}

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<small className="text-muted">Em caso de dúvidas consulte sempre a CIBio BASF – cibio@basf.com</small>
					</Form.Group>
				</Form.Row>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}
